// src/rms/hooks/useCollection.js
// ============================================================================
// Data loading for RMS screens
// ============================================================================
// Re-queries whenever the operator changes or `revision` is bumped by a write,
// which keeps screens in step without a global store.
// ============================================================================

import { useCallback, useEffect, useState } from "react";
import db from "../data";
import { useRms } from "../context/RmsContext";

/**
 * `options.skip` stops the query being issued at all.
 *
 * Not the same as ignoring the result. A screen that hides a figure it has
 * already fetched has fetched it -- the rows are in the browser and anybody
 * can read them. Skipping is what makes "this member does not see the money"
 * a statement about what was transferred rather than about what was rendered.
 */
export function useCollection(collection, where = {}, options = {}) {
  const { operator, revision } = useRms();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const whereKey = JSON.stringify(where);
  const optionsKey = JSON.stringify(options);

  const load = useCallback(async () => {
    if (!operator || JSON.parse(optionsKey).skip) {
      setRows([]);
      setLoading(false);
      return;
    }
    const result = await db.list(
      collection,
      { operator_id: operator.id, ...JSON.parse(whereKey) },
      JSON.parse(optionsKey)
    );
    setRows(result.success ? result.data : []);
    setLoading(false);
  }, [collection, operator, whereKey, optionsKey]);

  useEffect(() => {
    load();
  }, [load, revision]);

  return { rows, loading, reload: load };
}

/** Index a row list by id for cheap lookups when joining in the UI. */
export function indexById(rows) {
  return rows.reduce((acc, r) => {
    acc[r.id] = r;
    return acc;
  }, {});
}

export default useCollection;
